"use client";

import { useRef } from "react";
import {
  motion,
  useScroll,
  useVelocity,
  useSpring,
  useTransform,
  useMotionValue,
  useAnimationFrame,
  wrap,
} from "framer-motion";
import { cn } from "@/lib/utils";

interface VelocityMarqueeProps {
  items: string[];
  /** Idle drift speed (% of width per second). Negative scrolls left. */
  baseVelocity?: number;
  className?: string;
  /** Render text as an outlined (stroked) variant. */
  outline?: boolean;
}

/**
 * A horizontally-scrolling marquee whose speed and direction react to the
 * page's scroll velocity — flick the page and the words surge, à la Framer's
 * signature scroll-velocity text. Loops seamlessly via four repeated copies.
 */
export function VelocityMarquee({
  items,
  baseVelocity = -4,
  className,
  outline = false,
}: VelocityMarqueeProps) {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], {
    clamp: false,
  });

  // Each copy is 25% of the track; wrap keeps us within one copy's width.
  const x = useTransform(baseX, (v) => `${wrap(-25, 0, v)}%`);

  const directionFactor = useRef(1);
  useAnimationFrame((_, delta) => {
    let moveBy = directionFactor.current * baseVelocity * (delta / 1000);
    const factor = velocityFactor.get();
    if (factor < 0) directionFactor.current = -1;
    else if (factor > 0) directionFactor.current = 1;
    moveBy += directionFactor.current * moveBy * factor;
    baseX.set(baseX.get() + moveBy);
  });

  const content = (
    <span
      className="flex shrink-0 items-center"
      style={
        outline
          ? {
              WebkitTextStroke: "1.5px currentColor",
              color: "transparent",
            }
          : undefined
      }
    >
      {items.map((item, i) => (
        <span key={i} className="flex shrink-0 items-center">
          {item}
          <span aria-hidden className="mx-6 text-brand-400/70">
            ✦
          </span>
        </span>
      ))}
    </span>
  );

  return (
    <div className={cn("flex overflow-hidden whitespace-nowrap", className)}>
      <motion.div
        className="flex flex-nowrap whitespace-nowrap font-heading font-extrabold uppercase tracking-tight"
        style={{ x }}
      >
        {content}
        {content}
        {content}
        {content}
      </motion.div>
    </div>
  );
}
